import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Link,
  IconButton,
  Stack,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  LinkedIn,
  Twitter,
  Facebook,
  Instagram,
  Email,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

// Color Palette consistent with Navbar
const footerColors = {
  primary: '#0077be',       // Professional Ocean Blue
  secondary: '#00a4cc',     // Clean Water Teal
  background: '#f0f4f8',    // Light, clean background
  text: {
    primary: '#1a2b3c',     // Deep navy for text
    secondary: '#4a6f8a'    // Soft blue-gray
  },
  accent: '#40e0d0',        // Bright accent for highlights
  yellow: '#FFD700'         // Highlight yellow
};

const FooterContainer = styled(Box)(({ theme }) => ({
  backgroundColor: footerColors.background,
  color: footerColors.text.primary,
  padding: theme.spacing(4, 0),
  borderTop: `1px solid ${footerColors.primary}10`,
}));

const FooterLink = styled(Link)(({ theme }) => ({
  color: footerColors.text.secondary,
  textDecoration: 'none',
  transition: 'color 0.3s ease',
  fontFamily: '"Inter", "Segoe UI", Arial, sans-serif',
  fontSize: '14px',
  '&:hover': {
    color: footerColors.primary,
    textDecoration: 'underline',
  },
}));

const SocialIconButton = styled(IconButton)(({ theme }) => ({
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
  '&.linkedin': {
    color: '#0077B5', // LinkedIn blue
  },
  '&.twitter': {
    color: '#1DA1F2', // Twitter blue
  },
  '&.facebook': {
    color: '#1877F2', // Facebook blue
  },
  '&.instagram': {
    color: '#E4405F', // Instagram pink-red
  },
}));

const Footer = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const currentYear = new Date().getFullYear();

  const footerSections = [
    {
      title: 'Quick Links',
      links: [
        { name: 'Home', path: '/' },
        { name: 'About Us', path: '/about' },
        { name: 'Solutions', path: '/solutions' },
        { name: 'Projects', path: '/projects' },
        { name: 'Careers', path: '/careers' },
        { name: 'Contact', path: '/contact' },
      ]
    },
    {
      title: 'Solutions',
      links: [
        { name: 'SCADA System', path: '/solutions/scada' },
        { name: 'IoT Platform', path: '/solutions/iot' },
        { name: 'Cloud Services', path: '/solutions/cloud' },
        { name: 'RO Treatment', path: '/solutions/ro-treatment' },
      ]
    },
    {
      title: 'Products',
      links: [
        { name: 'Waste Water Treatment', path: '/products/waste-water-treatment' },
        { name: 'Equipment & Systems', path: '/products/equipment-systems' },
        { name: 'Accessories', path: '/products/accessories' },
        { name: 'Specialty Chemicals', path: '/products/specialty-chemicals' },
      ]
    }
  ];

  return (
    <FooterContainer>
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Company Info Column */}
          <Grid item xs={12} md={4}>
            <Box sx={{ mb: 2 }}>
              <img
                src="/images/logo.png"
                alt="WTT International Logo"
                style={{ 
                  height: '60px', 
                  marginBottom: '16px',
                  borderRadius: '8px'
                }}
              />
              <Typography 
                variant="body2" 
                sx={{ 
                  color: footerColors.text.secondary, 
                  mb: 2,
                  lineHeight: 1.6 
                }}
              >
                Transforming water treatment through cutting-edge SCADA, IoT, and sustainable technologies.
              </Typography>
            </Box>
            
            {/* Contact Info */}
            <Stack spacing={1}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Phone sx={{ color: footerColors.primary, fontSize: 20 }} />
                <Typography variant="body2">0421 653 1236</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Email sx={{ color: footerColors.primary, fontSize: 20 }} />
                <Typography variant="body2">enquiry@wttint.com</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <LocationOn sx={{ color: footerColors.primary, fontSize: 20 }} />
                <Typography variant="body2">
                  No.3, college, Cross Road, Avarangadu, Tiruppur, Tamil Nadu 641602
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Quick Links Column */}
          <Grid item xs={12} md={2.5}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: footerColors.text.primary 
              }}
            >
              Quick Links
            </Typography>
            <Stack spacing={1}>
              {footerSections[0].links.map((link) => (
                <FooterLink
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                >
                  {link.name}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Solutions Column */}
          <Grid item xs={12} md={2.5}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: footerColors.text.primary 
              }}
            >
              Solutions
            </Typography>
            <Stack spacing={1}>
              {footerSections[1].links.map((link) => (
                <FooterLink
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                >
                  {link.name}
                </FooterLink>
              ))}
            </Stack>
          </Grid>

          {/* Products Column */}
          <Grid item xs={12} md={3}>
            <Typography 
              variant="h6" 
              sx={{ 
                mb: 2, 
                fontWeight: 600, 
                color: footerColors.text.primary 
              }}
            >
              Products
            </Typography>
            <Stack spacing={1}>
              {footerSections[2].links.map((link) => (
                <FooterLink
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                >
                  {link.name}
                </FooterLink>
              ))}
            </Stack>
          </Grid>
        </Grid>

        {/* Connect With Us and Newsletter Section */}
        <Divider sx={{ my: 3, borderColor: footerColors.primary + '30' }} />
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between', 
          alignItems: 'center',
          gap: 2,
          py: 2,
          px: 4,
          backgroundColor: '#f4f4f4',
          borderRadius: 2
        }}>
          {/* Connect With Us */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-start'
          }}>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: footerColors.text.primary,
                mr: 2
              }}
            >
              Connect With Us
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <SocialIconButton 
                className="linkedin"
                href="https://www.linkedin.com/company/wtt-international" 
                target="_blank" 
                aria-label="LinkedIn"
              >
                <LinkedIn />
              </SocialIconButton>
              <SocialIconButton 
                className="twitter"
                href="https://twitter.com/wttinternational" 
                target="_blank" 
                aria-label="Twitter"
              >
                <Twitter />
              </SocialIconButton>
              <SocialIconButton 
                className="facebook"
                href="https://www.facebook.com/wttinternational" 
                target="_blank" 
                aria-label="Facebook"
              >
                <Facebook />
              </SocialIconButton>
              <SocialIconButton 
                className="instagram"
                href="https://www.instagram.com/wttinternational" 
                target="_blank" 
                aria-label="Instagram"
              >
                <Instagram />
              </SocialIconButton>
            </Box>
          </Box>

          {/* Newsletter Subscription */}
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 2,
            width: isMobile ? '100%' : 'auto',
            justifyContent: isMobile ? 'center' : 'flex-end'
          }}>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 500, 
                color: footerColors.text.secondary,
                mr: 2
              }}
            >
              Subscribe to Our Newsletter
            </Typography>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: 1,
              backgroundColor: 'white',
              borderRadius: 2,
              p: 0.5,
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
            }}>
              <input 
                type="email" 
                placeholder="Enter your email" 
                style={{
                  border: 'none',
                  outline: 'none',
                  padding: '8px',
                  width: '200px',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button 
                style={{
                  backgroundColor: footerColors.primary,
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  cursor: 'pointer',
                  transition: 'background-color 0.3s ease'
                }}
                onMouseOver={(e) => e.target.style.backgroundColor = footerColors.secondary}
                onMouseOut={(e) => e.target.style.backgroundColor = footerColors.primary}
              >
                Subscribe
              </button>
            </Box>
          </Box>
        </Box>

        {/* Copyright */}
        <Box 
          sx={{ 
            textAlign: 'center', 
            mt: 3, 
            pt: 2, 
            borderTop: `1px solid ${footerColors.primary}20` 
          }}
        >
          <Typography variant="body2" color="text.secondary">
            © {currentYear} WTT International. All Rights Reserved.
          </Typography>
          <Box sx={{ mt: 1 }}>
            <FooterLink 
              component={RouterLink} 
              to="/privacy-policy" 
              sx={{ mr: 2 }}
            >
              Privacy Policy
            </FooterLink>
            <FooterLink 
              component={RouterLink} 
              to="/terms-of-service"
            >
              Terms of Service
            </FooterLink>
          </Box>
        </Box>
      </Container>
    </FooterContainer>
  );
};

export default Footer;
